import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Checkbox } from '@business/workflows/ComponentUI';
import { SVGAssets } from '@business/assets/vectors';
import { Filters } from '@business/workflows/ComponentUI/Filters';
import { SearchInput } from '@business/workflows/ComponentUI/HierarchicalMultiSelect/search';
import { useAssetSearchMutation } from '@ports/internal-services/asset/assetSlice';

interface SelectGalleryModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (
    selectedItems: Array<{ id: string; url: string; type?: string }>
  ) => void;
  multiSelect?: boolean;
  setAssetIds?: React.Dispatch<
    React.SetStateAction<Array<{ id: string; order: number }>>
  >;
  isFromAddMemory?: boolean;
}

export const SelectGalleryModal: React.FC<SelectGalleryModalProps> = ({
  isOpen,
  onClose,
  onSelect,
  multiSelect = true,
  setAssetIds,
  isFromAddMemory = false
}) => {
  const { t } = useTranslation();
  const modalRef = useRef<HTMLDivElement>(null);
  const [selectedPhotos, setSelectedPhotos] = useState<
    Array<{ id: string; url: string; type?: string }>
  >([]);
  const [searchResults, setSearchResults] = useState<any>(null);
  const [filterOptions, setFilterOptions] = useState<any>({});
  const [selectedView, setSelectedView] = useState<'grid' | 'list'>('grid');
  const [accessType, setAccessType] = useState<'public' | 'private'>('public');
  const [isAccessDropdownOpen, setIsAccessDropdownOpen] =
    useState<boolean>(false);
  const accessDropdownRef = useRef<HTMLDivElement>(null);

  const [AssetSearch] = useAssetSearchMutation();

  useEffect(() => {
    if (isOpen) {
      assetFiltersHandler();
    }
  }, [isOpen, filterOptions, accessType]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        accessDropdownRef.current &&
        !accessDropdownRef.current.contains(event.target as Node)
      ) {
        setIsAccessDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const assetFiltersHandler = () => {
    let sortBy = 'createdAt';
    let orderBy = 'desc';

    if (filterOptions?.sortBy) {
      const sortValue = filterOptions.sortBy;
      if (sortValue === 'mostRecent') {
        sortBy = 'createdAt';
        orderBy = 'Desc';
      } else if (sortValue === 'leastRecent') {
        sortBy = 'createdAt';
        orderBy = 'Asc';
      }
    }

    const dataObj = {
      assetSearch: {
        search: filterOptions?.search,
        sortBy: sortBy,
        orderBy: orderBy,
        favorites: filterOptions?.favorites || false,
        sharedWith: filterOptions?.sharedWith || '',
        people: filterOptions?.people || '',
        things: filterOptions?.things || '',
        locations: filterOptions?.locations || '',
        dates: filterOptions?.date || '',
        interactions: filterOptions?.interactions || '',
        mediaType:
          filterOptions?.mediaType?.length > 0
            ? filterOptions?.mediaType
            : isFromAddMemory
              ? ['image', 'video']
              : undefined,
        access: accessType === 'public' ? 'public' : 'private'
      }
    };

    AssetSearch(dataObj)
      .then((res: any) => {
        if (res?.data) {
          setSearchResults(res?.data?.data);
        }
      })
      .catch(err => {
        console.error('Error searching assets:', err);
      });
  };

  const handlePhotoSelect = (
    photoId: string,
    photoUrl: string,
    photoType?: string
  ) => {
    if (multiSelect) {
      setSelectedPhotos(prev => {
        const exists = prev.some(item => item.id === photoId);
        if (exists) {
          return prev.filter(item => item.id !== photoId);
        } else {
          return [...prev, { id: photoId, url: photoUrl, type: photoType }];
        }
      });
    } else {
      setSelectedPhotos([{ id: photoId, url: photoUrl, type: photoType }]);
    }
  };

  const handleClose = () => {
    setSelectedPhotos([]);
    onClose();
  };

  const handleUpload = () => {
    // Merge new asset IDs with existing ones (passed via setAssetIds)
    if (setAssetIds) {
      setAssetIds((prevIds: Array<{ id: string; order: number }>) => {
        // Get the highest order from existing IDs
        const maxOrder =
          prevIds.length > 0 ? Math.max(...prevIds.map(item => item.order)) : 0;

        // Combine previous IDs with newly selected ones, removing duplicates
        const newIds = selectedPhotos.map((item, index) => ({
          id: item.id,
          order: maxOrder + index + 1
        }));

        // Filter out duplicates based on id
        const existingIds = new Set(prevIds.map(item => item.id));
        const uniqueNewIds = newIds.filter(item => !existingIds.has(item.id));

        return [...prevIds, ...uniqueNewIds];
      });
    }
    onSelect(selectedPhotos);
    setSelectedPhotos([]);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
      <div
        ref={modalRef}
        className="bg-white rounded-2xl shadow-lg relative h-[90vh] flex flex-col w-[1100px] max-w-[95vw]">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between flex-shrink-0">
          <h2 className="text-xl !font-semibold text-[#353535]">
            {t('selectGallery.title')}
          </h2>
          <Button
            btnStyle="link"
            className="text-gray-500 hover:text-gray-700"
            onClick={handleClose}>
            <SVGAssets.CloseIconGray className="w-5 h-5" />
          </Button>
        </div>

        {/* Filter Section */}
        <div className="px-6 py-2 border-b border-gray-200 flex-shrink-0">
          <h3 className="text-sm font-semibold text-gray-700 mb-1">
            {t('selectGallery.filterBy')}
          </h3>
          <Filters
            setFilterOptions={setFilterOptions}
            selectedView={selectedView}
            onViewChange={setSelectedView}
            filterOptions={filterOptions}
            module="assets"
            isFromAddMemory={isFromAddMemory}
          />
          <SearchInput
            className=""
            value={filterOptions.search || ''}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              setFilterOptions({
                ...filterOptions,
                search: e.target.value
              });
            }}
            onCameraClick={() => {
              console.log('camera click');
            }}
          />
        </div>

        {/* Gallery Header */}
        <div className="px-6 py-2 border-b border-gray-200 flex items-center justify-between flex-shrink-0">
          <div className="flex items-center gap-2">
            <h3 className="text-base font-semibold text-gray-800">
              {t('photo')}
            </h3>
            {selectedPhotos.length > 0 && (
              <span className="text-sm text-gray-600">
                ({t('selectGallery.selected', { count: selectedPhotos.length })}
                )
              </span>
            )}
          </div>
          <div className="relative" ref={accessDropdownRef}>
            <Button
              onClick={() => setIsAccessDropdownOpen(!isAccessDropdownOpen)}
              btnStyle="no-outline"
              className="px-3 py-2 text-sm bg-white text-gray-700 rounded-md focus:outline-none cursor-pointer hover:bg-gray-50 flex items-center gap-2 min-w-[120px] justify-between">
              <div className="flex items-center gap-2">
                {accessType === 'private' ? (
                  <SVGAssets.LockClosedIcon />
                ) : (
                  <SVGAssets.LockOpen />
                )}
                <span>
                  {accessType === 'public'
                    ? t('photosPage.public')
                    : t('photosPage.private')}
                </span>
              </div>
              <svg
                className={`w-4 h-4 transition-transform ${isAccessDropdownOpen ? 'rotate-180' : ''}`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19 9l-7 7-7-7"
                />
              </svg>
            </Button>

            {isAccessDropdownOpen && (
              <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg border border-gray-200 z-50">
                <div className="py-1">
                  <button
                    onClick={() => {
                      setAccessType('public');
                      setIsAccessDropdownOpen(false);
                    }}
                    className={`w-full px-4 py-2 text-sm text-left hover:bg-gray-50 flex items-center gap-2 ${
                      accessType === 'public' ? 'bg-gray-50' : ''
                    }`}>
                    <SVGAssets.LockOpen />
                    <span>{t('photosPage.public')}</span>
                  </button>
                  <button
                    onClick={() => {
                      setAccessType('private');
                      setIsAccessDropdownOpen(false);
                    }}
                    className={`w-full px-4 py-2 text-sm text-left hover:bg-gray-50 flex items-center gap-2 ${
                      accessType === 'private' ? 'bg-gray-50' : ''
                    }`}>
                    <SVGAssets.LockClosedIcon />
                    <span>{t('photosPage.private')}</span>
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Gallery Content */}
        <div className="flex-1 overflow-y-auto px-6 py-2">
          {searchResults?.rows && searchResults.rows.length > 0 ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4">
              {searchResults.rows.map((photo: any, index: number) => (
                <div
                  key={photo?.id || index}
                  className="relative group cursor-pointer rounded-lg overflow-hidden bg-white border border-gray-200 hover:shadow-lg transition-shadow"
                  onClick={() =>
                    handlePhotoSelect(
                      photo?.id,
                      photo?.thumbNailUrl,
                      photo?.type
                    )
                  }>
                  <div className="aspect-square overflow-hidden rounded-lg">
                    {photo?.type === 'audio' ? (
                      <div className="w-full h-full flex items-center justify-center bg-gray-300 hover:bg-gray-200 transition-colors">
                        <SVGAssets.Audio className="w-12 h-12 text-gray-400" />
                      </div>
                    ) : (
                      <>
                        <img
                          src={photo?.thumbNailUrl || '/placeholder-image.jpg'}
                          alt={photo?.name || `Photo ${index + 1}`}
                          className="w-full h-full object-cover"
                        />
                        {/* Play icon for videos */}
                        {(photo?.type === 'video' ||
                          photo?.type?.startsWith('video/')) && (
                          <div className="absolute inset-0 flex items-center justify-center">
                            <div className="w-12 h-12 bg-black/60 rounded-full flex items-center justify-center">
                              <SVGAssets.PlayButton className="w-6 h-6 text-white ml-1" />
                            </div>
                          </div>
                        )}
                      </>
                    )}
                  </div>

                  {/* Checkbox overlay */}
                  <div
                    className="absolute top-2 left-2 z-[5]"
                    onClick={e => e.stopPropagation()}>
                    <Checkbox
                      name={`${photo?.id}`}
                      checked={selectedPhotos.some(
                        item => item.id === photo?.id
                      )}
                      onChange={e => {
                        e.stopPropagation();
                        handlePhotoSelect(
                          photo?.id,
                          photo?.thumbNailUrl,
                          photo?.type
                        );
                      }}
                      className="w-5 h-5 text-[#7F9D48] rounded focus:ring-[#7F9D48] focus:ring-2"
                    />
                  </div>

                  {/* Selection overlay */}
                  {selectedPhotos.some(item => item.id === photo.id) && (
                    <div className="absolute inset-0 bg-[#7F9D48]/20 border-2 border-[#7F9D48] rounded-lg" />
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <SVGAssets.CameraIcon className="w-20 h-20 text-gray-300 mb-4" />
              <p className="text-gray-500 text-lg font-medium">
                {t('selectGallery.noResults')}
              </p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-2 border-t border-gray-200 flex justify-end gap-3 flex-shrink-0">
          <Button onClick={handleClose} className="cancel-btn !text-xs">
            {t('selectGallery.cancel')}
          </Button>
          <Button
            btnStyle="primary"
            onClick={handleUpload}
            disabled={selectedPhotos.length === 0}
            className="bg-[#7F9D48] text-white font-medium px-6 py-2 rounded-md hover:bg-[#6d8c3e] transition disabled:opacity-50 disabled:cursor-not-allowed !text-xs">
            {t('selectGallery.upload')}
          </Button>
        </div>
      </div>
    </div>
  );
};
